import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
    Camera, Film, Tv, Heart, Share2, Video, Music, Edit, Search,
    Filter, Grid, List, X, Play, Bookmark, MoreVertical,
    Eye, Star, Clock, Users, ChevronDown, ArrowUp, Check,
    MessageCircle, Download, Flag, Plus, AlertCircle
} from 'lucide-react';
import Confetti from 'react-confetti';
import '../styles/film-community.css';
import CommunityCreator from '../components/Communitycreator';
import CommunitySwitcher from '../components/CommunitySwitchers';
import PostCreator from '../components/CommunityPostCreatoor';
import Nav from '../components/nav';
import SEO from '../components/SEO';
import { baseUrl } from '../config';

// API Service Functions
const API = {
    getAuthHeaders: () => {
        const token = localStorage.getItem("access_token");
        return {
            'Content-Type': 'application/json',
            ...(token && { 'Authorization': `Bearer ${token}` })
        };
    },

    community: {
        list: async () => {
            try {
                const response = await fetch(`${baseUrl}/community`, {
                    headers: API.getAuthHeaders()
                });
                if (!response.ok) return [];
                return response.json();
            } catch (error) {
                return [];
            }
        },

        join: async (communityId) => {
            const response = await fetch(`${baseUrl}/community/${communityId}/join`, {
                method: 'POST',
                headers: API.getAuthHeaders()
            });
            return response.json();
        }
    },

    posts: {
        list: async () => {
            try {
                const response = await fetch(`${baseUrl}/community-post/list`, {
                    headers: API.getAuthHeaders()
                });
                if (!response.ok) {
                    return [];
                }
                const data = await response.json();
                return data;
            } catch (error) {
                return [];
            }
        },

        listByCommunity: async (communityId) => {
            try {
                const response = await fetch(`${baseUrl}/community-post/list/by-community/${communityId}`, {
                    headers: API.getAuthHeaders()
                });
                if (!response.ok) {
                    return [];
                }
                const data = await response.json();
                return data.posts || [];
            } catch (error) {
                return [];
            }
        },

        like: async (postId) => {
            const response = await fetch(`${baseUrl}/community-post/${postId}/like`, {
                method: 'POST',
                headers: API.getAuthHeaders()
            });
            return response.json();
        },

        unlike: async (postId) => {
            const response = await fetch(`${baseUrl}/community-post/${postId}/unlike`, {
                method: 'DELETE',
                headers: API.getAuthHeaders()
            });
            return response.json();
        },

        bookmark: async (postId, notes = null) => {
            const response = await fetch(`${baseUrl}/community-post/${postId}/bookmark`, {
                method: 'POST',
                headers: API.getAuthHeaders(),
                body: JSON.stringify({ notes })
            });
            return response.json();
        },

        removeBookmark: async (postId) => {
            const response = await fetch(`${baseUrl}/community-post/${postId}/remove-bookmark`, {
                method: 'DELETE',
                headers: API.getAuthHeaders()
            });
            return response.json();
        },

        share: async (postId, platform) => {
            const response = await fetch(`${baseUrl}/community-post/${postId}/share`, {
                method: 'POST',
                headers: API.getAuthHeaders(),
                body: JSON.stringify({ platform })
            });
            return response.json();
        }
    }
};

// Toast Component
const Toast = ({ message, type, onClose }) => (
    <div className={`toast toast-${type}`}>
        <div className="flex items-center gap-2">
            <Check className="h-5 w-5" />
            <span>{message}</span>
        </div>
        <button onClick={onClose} className="ml-4">
            <X className="h-4 w-4" />
        </button>
    </div>
);

Toast.propTypes = {
    message: PropTypes.string.isRequired,
    type: PropTypes.oneOf(['success', 'error', 'info']),
    onClose: PropTypes.func.isRequired,
};

// Skeleton Card
const SkeletonCard = () => (
    <div className="skeleton-card">
        <div className="skeleton-thumbnail"></div>
        <div className="skeleton-content">
            <div className="skeleton-title"></div>
            <div className="skeleton-text"></div>
        </div>
    </div>
);

// Card Components
const CardContent = ({ children, className }) => (
    <div className={`p-4 ${className}`}>{children}</div>
);

CardContent.propTypes = {
    children: PropTypes.node.isRequired,
    className: PropTypes.string,
};

const Card = ({ children, className, onMouseEnter, onMouseLeave, onClick }) => (
    <div
        className={`project-card enhanced ${className}`}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        onClick={onClick}
        tabIndex="0"
        role="button"
    >
        {children}
    </div>
);

Card.propTypes = {
    children: PropTypes.node.isRequired,
    className: PropTypes.string,
    onMouseEnter: PropTypes.func,
    onMouseLeave: PropTypes.func,
    onClick: PropTypes.func,
};

// Badge
const Badge = ({ children, className, type = 'purple' }) => (
    <span className={`badge badge-${type} ${className}`}>
        {children}
    </span>
);

Badge.propTypes = {
    children: PropTypes.node.isRequired,
    className: PropTypes.string,
    type: PropTypes.oneOf(['purple', 'yellow', 'green', 'blue', 'red']),
};

// Quick Actions Menu
const QuickActionsMenu = ({ projectId, onAction }) => {
    const [isOpen, setIsOpen] = useState(false);

    const actions = [
        { icon: Share2, label: 'Share', action: 'share' },
        { icon: Bookmark, label: 'Save to Playlist', action: 'save' },
        { icon: Download, label: 'Download Info', action: 'download' },
        { icon: Flag, label: 'Report', action: 'report' }
    ];

    return (
        <div className="quick-actions-menu">
            <button
                className="menu-trigger"
                onClick={(e) => {
                    e.stopPropagation();
                    setIsOpen(!isOpen);
                }}
            >
                <MoreVertical className="h-5 w-5" />
            </button>
            {isOpen && (
                <div className="menu-dropdown">
                    {actions.map((action, idx) => (
                        <button
                            key={idx}
                            className="menu-item"
                            onClick={(e) => {
                                e.stopPropagation();
                                onAction(action.action, projectId);
                                setIsOpen(false);
                            }}
                        >
                            <action.icon className="h-4 w-4" />
                            <span>{action.label}</span>
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
};

QuickActionsMenu.propTypes = {
    projectId: PropTypes.number.isRequired,
    onAction: PropTypes.func.isRequired,
};

// Video Preview Modal
const VideoPreviewModal = ({ project, onClose }) => {
    if (!project) return null;

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                <button className="modal-close" onClick={onClose}>
                    <X className="h-6 w-6" />
                </button>

                <div className="modal-video">
                    <div className="video-placeholder">
                        <Play className="h-16 w-16 text-white" />
                        <p className="text-white mt-4">Video Preview</p>
                    </div>
                </div>

                <div className="modal-details">
                    <div className="flex justify-between items-start mb-4">
                        <div>
                            <h2 className="text-3xl font-bold mb-2">{project.title}</h2>
                            <p className="text-gray-600">Created by {project.author || 'Unknown'}</p>
                        </div>
                        <div className="flex gap-2">
                            {project.community_name && (
                                <Badge type="purple">{project.community_name}</Badge>
                            )}
                        </div>
                    </div>

                    <div className="stats-row mb-4">
                        <div className="stat-item-large">
                            <Heart className="h-5 w-5" />
                            <span>{project.like_count || 0}</span>
                        </div>
                        <div className="stat-item-large">
                            <MessageCircle className="h-5 w-5" />
                            <span>{project.comment_count || 0}</span>
                        </div>
                        <div className="stat-item-large">
                            <Share2 className="h-5 w-5" />
                            <span>{project.share_count || 0}</span>
                        </div>
                    </div>

                    {project.content && (
                        <div>
                            <h3 className="font-semibold mb-2">Description</h3>
                            <p className="text-gray-600">{project.content}</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

VideoPreviewModal.propTypes = {
    project: PropTypes.object,
    onClose: PropTypes.func.isRequired,
};

// Filter Sidebar
const FilterSidebar = ({ isOpen, onClose, filters, onFilterChange }) => {
    const genres = ['Action', 'Drama', 'Comedy', 'Sci-Fi', 'Documentary', 'Horror', 'Romance', 'Thriller'];
    const durations = ['0-5 min', '5-15 min', '15-30 min', '30+ min', 'Feature Length'];
    const statuses = ['In Production', 'Post-Production', 'Completed', 'Pre-Production'];

    return (
        <div className={`filter-sidebar ${isOpen ? 'open' : ''}`}>
            <div className="filter-header">
                <h3 className="font-semibold text-lg">Filters</h3>
                <button onClick={onClose}>
                    <X className="h-5 w-5" />
                </button>
            </div>

            <div className="filter-section">
                <h4 className="filter-title">Genre</h4>
                {genres.map((genre) => (
                    <label key={genre} className="filter-checkbox">
                        <input
                            type="checkbox"
                            checked={filters.genres?.includes(genre)}
                            onChange={(e) => {
                                const newGenres = e.target.checked
                                    ? [...(filters.genres || []), genre]
                                    : filters.genres.filter(g => g !== genre);
                                onFilterChange({ ...filters, genres: newGenres });
                            }}
                        />
                        <span>{genre}</span>
                    </label>
                ))}
            </div>

            <div className="filter-section">
                <h4 className="filter-title">Duration</h4>
                {durations.map((duration) => (
                    <label key={duration} className="filter-checkbox">
                        <input
                            type="radio"
                            name="duration"
                            checked={filters.duration === duration}
                            onChange={() => onFilterChange({ ...filters, duration })}
                        />
                        <span>{duration}</span>
                    </label>
                ))}
            </div>

            <div className="filter-section">
                <h4 className="filter-title">Status</h4>
                {statuses.map((status) => (
                    <label key={status} className="filter-checkbox">
                        <input
                            type="checkbox"
                            checked={filters.statuses?.includes(status)}
                            onChange={(e) => {
                                const newStatuses = e.target.checked
                                    ? [...(filters.statuses || []), status]
                                    : filters.statuses.filter(s => s !== status);
                                onFilterChange({ ...filters, statuses: newStatuses });
                            }}
                        />
                        <span>{status}</span>
                    </label>
                ))}
            </div>

            <div className="filter-section">
                <h4 className="filter-title">Award Winning</h4>
                <label className="filter-checkbox">
                    <input
                        type="checkbox"
                        checked={filters.awardWinning}
                        onChange={(e) => onFilterChange({ ...filters, awardWinning: e.target.checked })}
                    />
                    <span>Show only award winners</span>
                </label>
            </div>

            <button className="filter-reset" onClick={() => onFilterChange({})}>
                Clear All Filters
            </button>
        </div>
    );
};

FilterSidebar.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    filters: PropTypes.object.isRequired,
    onFilterChange: PropTypes.func.isRequired,
};

// Featured Hero
const FeaturedHero = ({ project, onViewDetails }) => {
    if (!project) return null;

    return (
        <div className="featured-hero">
            <div className="featured-background">
                <img src={project.thumbnail_url || "/api/placeholder/1200/600"} alt={project.title} />
                <div className="featured-gradient"></div>
            </div>
            <div className="featured-content">
                <Badge type="yellow" className="mb-4">Featured Project</Badge>
                <h2 className="featured-title">{project.title}</h2>
                <p className="featured-director">{project.community_name || 'Community'}</p>
                <div className="featured-stats">
                    <div className="stat-item">
                        <Heart className="h-5 w-5" />
                        <span>{project.like_count || 0}</span>
                    </div>
                    <div className="stat-item">
                        <MessageCircle className="h-5 w-5" />
                        <span>{project.comment_count || 0}</span>
                    </div>
                    <div className="stat-item">
                        <Share2 className="h-5 w-5" />
                        <span>{project.share_count || 0}</span>
                    </div>
                </div>
                <div className="featured-actions">
                    <button className="featured-play" onClick={() => onViewDetails(project)}>
                        <Play className="h-5 w-5 mr-2" />
                        View Details
                    </button>
                    <button className="featured-info" onClick={() => onViewDetails(project)}>
                        More Info
                    </button>
                </div>
            </div>
        </div>
    );
};

FeaturedHero.propTypes = {
    project: PropTypes.object,
    onViewDetails: PropTypes.func.isRequired,
};

// Join Popup
const JoinCommunityPopup = ({ onClose, onJoin }) => (
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
        <div className="bg-white p-8 rounded-lg shadow-lg text-center max-w-md">
            <h2 className="text-4xl font-semibold mb-4">Join the Community</h2>
            <p className="mb-6">Become a part of our innovative film community and start collaborating with creators worldwide.</p>
            <button className="px-6 py-3 bg-purple-600 text-white rounded-full font-semibold hover:bg-purple-700 transition" onClick={onJoin}>
                Join Now
            </button>
            <button className="ml-4 px-6 py-3 bg-gray-300 text-gray-700 rounded-full font-semibold hover:bg-gray-400 transition" onClick={onClose}>
                Maybe Later
            </button>
        </div>
    </div>
);

JoinCommunityPopup.propTypes = {
    onClose: PropTypes.func.isRequired,
    onJoin: PropTypes.func.isRequired,
};

// Main Component
const FilmCommunityPage = ({ hideNav = false }) => {
    const [activeTab, setActiveTab] = useState('trending');
    const [hoveredProject, setHoveredProject] = useState(null);
    const [showPopup, setShowPopup] = useState(false);
    const [showConfetti, setShowConfetti] = useState(false);
    const [loading, setLoading] = useState(true);
    const [viewMode, setViewMode] = useState('grid');
    const [filterOpen, setFilterOpen] = useState(false);
    const [filters, setFilters] = useState({});
    const [selectedProject, setSelectedProject] = useState(null);
    const [likedProjects, setLikedProjects] = useState(new Set());
    const [bookmarkedProjects, setBookmarkedProjects] = useState(new Set());
    const [toast, setToast] = useState(null);
    const [showBackToTop, setShowBackToTop] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [visibleProjects, setVisibleProjects] = useState(6);
    const [sortBy, setSortBy] = useState('popular');
    const [allProjects, setAllProjects] = useState([]);
    const [communities, setCommunities] = useState([]);
    const [selectedCommunity, setSelectedCommunity] = useState(null);
    const [isMember, setIsMember] = useState(false);
    const [hasShownPopup, setHasShownPopup] = useState(false);
    
    // Community Creator State
    const [showCommunityCreator, setShowCommunityCreator] = useState(false);
    const [showPostCreator, setShowPostCreator] = useState(false);
    const [currentCommunityView, setCurrentCommunityView] = useState(null);
    const [communitySwitcherKey, setCommunitySwitcherKey] = useState(0);
    
    // ✅ NEW: Scroll state for header shrink effect
    const [isScrolled, setIsScrolled] = useState(false);

    const isAuthenticated = () => !!localStorage.getItem('access_token');

    const getCurrentCommunityName = () => {
        if (!currentCommunityView) return 'All Communities';
        const community = communities.find(c => c.community_id === currentCommunityView);
        return community?.name || 'Unknown Community';
    };

    const isCurrentCommunityMember = () => {
        if (!currentCommunityView || !isAuthenticated()) return false;
        return true;
    };

    useEffect(() => {
        loadCommunities();
        checkMembershipStatus();
    }, []);

    useEffect(() => {
        const popupShown = localStorage.getItem('communityPopupShown');
        if (isAuthenticated() && !isMember && !popupShown && !hasShownPopup) {
            const timer = setTimeout(() => {
                setShowPopup(true);
                setHasShownPopup(true);
            }, 3000);
            return () => clearTimeout(timer);
        }
    }, [isMember, hasShownPopup]);

    useEffect(() => {
        loadPosts();
    }, []);

    // ✅ NEW: Scroll effect for header shrinking
    useEffect(() => {
        const handleScroll = () => {
            const scrollPosition = window.scrollY;
            setShowBackToTop(scrollPosition > 500);
            setIsScrolled(scrollPosition > 100); // ✅ Shrink header after 100px scroll
        };
        
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const loadCommunities = async () => {
        try {
            const data = await API.community.list();
            if (Array.isArray(data)) {
                setCommunities(data);
                setCommunitySwitcherKey(prev => prev + 1);
                if (data.length > 0 && !currentCommunityView) {
                    setSelectedCommunity(data[0].community_id);
                }
            } else {
                setCommunities([]);
            }
        } catch (error) {
            setCommunities([]);
        }
    };

    const checkMembershipStatus = async () => {
        if (!isAuthenticated()) {
            setIsMember(false);
            return;
        }

        try {
            const membershipStatus = localStorage.getItem('communityMember');
            setIsMember(membershipStatus === 'true');
        } catch (error) {
        }
    };

    const loadPosts = async () => {
        setLoading(true);
        try {
            const data = await API.posts.list();
            setAllProjects(Array.isArray(data) ? data : []);
        } catch (error) {
            setAllProjects([]);
        } finally {
            setLoading(false);
        }
    };

    const loadPostsByCommunity = async (communityId) => {
        setLoading(true);
        try {
            const data = await API.posts.listByCommunity(communityId);
            setAllProjects(Array.isArray(data) ? data : []);
        } catch (error) {
            setAllProjects([]);
        } finally {
            setLoading(false);
        }
    };

    const handleJoinCommunity = async () => {
        if (!isAuthenticated()) {
            showToast('Please login to join the community', 'error');
            return;
        }

        try {
            if (selectedCommunity) {
                await API.community.join(selectedCommunity);
                setIsMember(true);
                localStorage.setItem('communityMember', 'true');
                localStorage.setItem('communityPopupShown', 'true');
                setShowPopup(false);
                setShowConfetti(true);
                showToast('Welcome to the community! 🎬', 'success');
                setTimeout(() => setShowConfetti(false), 5000);
            }
        } catch (error) {
            showToast('Failed to join community', 'error');
        }
    };

    const handleClosePopup = () => {
        setShowPopup(false);
        localStorage.setItem('communityPopupShown', 'true');
    };

    const showToast = (message, type = 'success') => {
        setToast({ message, type });
        setTimeout(() => setToast(null), 3000);
    };

    const handleLike = async (postId) => {
        if (!isAuthenticated()) {
            showToast('Please login to like posts', 'error');
            return;
        }

        try {
            const isLiked = likedProjects.has(postId);
            if (isLiked) {
                await API.posts.unlike(postId);
                const newLiked = new Set(likedProjects);
                newLiked.delete(postId);
                setLikedProjects(newLiked);
            } else {
                await API.posts.like(postId);
                const newLiked = new Set(likedProjects);
                newLiked.add(postId);
                setLikedProjects(newLiked);
            }
            
            if (currentCommunityView) {
                loadPostsByCommunity(currentCommunityView);
            } else {
                loadPosts();
            }
        } catch (error) {
        }
    };

    const handleBookmark = async (postId) => {
        if (!isAuthenticated()) {
            showToast('Please login to bookmark posts', 'error');
            return;
        }

        try {
            const isBookmarked = bookmarkedProjects.has(postId);
            if (isBookmarked) {
                await API.posts.removeBookmark(postId);
                const newBookmarked = new Set(bookmarkedProjects);
                newBookmarked.delete(postId);
                setBookmarkedProjects(newBookmarked);
            } else {
                await API.posts.bookmark(postId);
                const newBookmarked = new Set(bookmarkedProjects);
                newBookmarked.add(postId);
                setBookmarkedProjects(newBookmarked);
            }
        } catch (error) {
        }
    };

    const handleQuickAction = async (action, projectId) => {
        if (!isAuthenticated() && action !== 'share') {
            showToast('Please login to perform this action', 'error');
            return;
        }

        try {
            switch (action) {
                case 'share':
                    await API.posts.share(projectId, 'web');
                    showToast('Share link copied! 🔗', 'success');
                    break;
                case 'save':
                    await handleBookmark(projectId);
                    break;
                case 'download':
                    showToast('Download started 📥', 'success');
                    break;
                case 'report':
                    showToast('Report submitted. Thank you!', 'info');
                    break;
                default:
                    break;
            }
        } catch (error) {
        }
    };

    const scrollToTop = () => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const loadMoreProjects = () => {
        setVisibleProjects(prev => prev + 6);
    };

    const categories = [
        { icon: Film, label: "Feature Films" },
        { icon: Video, label: "Short Films" },
        { icon: Camera, label: "Documentary" },
        { icon: Tv, label: "Series" },
        { icon: Music, label: "Music Videos" },
        { icon: Edit, label: "Animation" }
    ];

    const sortOptions = [
        { value: 'popular', label: 'Most Popular' },
        { value: 'recent', label: 'Most Recent' },
        { value: 'views', label: 'Most Viewed' },
        { value: 'rating', label: 'Highest Rated' }
    ];

    const getFilteredProjects = () => {
        let filtered = [...allProjects];
        if (searchQuery) {
            filtered = filtered.filter(project =>
                project.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                project.content?.toLowerCase().includes(searchQuery.toLowerCase())
            );
        }
        return filtered.slice(0, visibleProjects);
    };

    const displayedProjects = getFilteredProjects();

    return (
        <>
            <SEO
              title="Film Community"
              description="Join the ProducerBuddy film community — connect with directors, producers, writers, cinematographers, and crew from across the global film industry."
              url="https://producerbuddy.pilomation.com/Comunity"
            />
            {!hideNav && <Nav />}
            <div className="min-h-screen bg-white relative">
                {showPopup && (
                    <JoinCommunityPopup
                        onClose={handleClosePopup}
                        onJoin={handleJoinCommunity}
                    />
                )}
                {showConfetti && <Confetti />}
                {toast && (
                    <Toast
                        message={toast.message}
                        type={toast.type}
                        onClose={() => setToast(null)}
                    />
                )}
                {selectedProject && (
                    <VideoPreviewModal
                        project={selectedProject}
                        onClose={() => setSelectedProject(null)}
                    />
                )}

                {/* ✅ Hero Section - WITH SCROLL EFFECT */}
                <div className={`film-community-hero transition-all duration-300 ${isScrolled ? 'hero-shrink' : ''}`}>
                    <div className="container mx-auto px-8 relative z-10">
                        {/* Category Icons */}
                        <div className={`flex justify-center gap-6 mb-8 flex-wrap transition-all duration-300 ${isScrolled ? 'opacity-0 h-0 overflow-hidden mb-0' : 'opacity-100'}`}>
                            {categories.map((category, idx) => (
                                <div key={idx} className="category-icon-wrapper">
                                    <category.icon className="h-6 w-6 text-purple-600" />
                                    <p className="mt-2 text-sm text-gray-600">{category.label}</p>
                                </div>
                            ))}
                        </div>
                        
                        {/* Hero Title */}
                        <h1 className={`hero-title text-center transition-all duration-300 ${isScrolled ? 'text-3xl mb-4' : ''}`}>
                            Where Vision Meets Reality
                        </h1>
                        <p className={`hero-description text-center transition-all duration-300 ${isScrolled ? 'text-sm mb-4 opacity-0 h-0 overflow-hidden' : 'opacity-100'}`}>
                            Join the world's most innovative film community. Collaborate, create, and showcase your masterpiece.
                        </p>
                        
                        {/* Action Buttons */}
                        <div className={`flex justify-center gap-4 flex-wrap transition-all duration-300 ${isScrolled ? 'opacity-0 h-0 overflow-hidden mb-0' : 'opacity-100'}`}>
                            <button 
                                className="create-button px-8 py-4 rounded-full font-semibold text-white bg-teal-600 hover:bg-teal-700 transition-all duration-200"
                                onClick={() => {
                                    if (!isAuthenticated()) {
                                        showToast('Please login to create a community', 'error');
                                        return;
                                    }
                                    setShowCommunityCreator(!showCommunityCreator);
                                }}
                            >
                                <Plus className="h-5 w-5 inline mr-2" />
                                {showCommunityCreator ? 'Hide Creator' : 'Start Creating'}
                            </button>
                            <button className="explore-button px-8 py-4 rounded-full font-semibold">
                                Explore Projects
                            </button>
                        </div>
                    </div>
                </div>

                {/* ✅ Community Context Banner */}
                {currentCommunityView && (
                    <div className="bg-gradient-to-r from-teal-50 to-cyan-50 border-b-2 border-teal-200">
                        <div className="container mx-auto px-8 py-4">
                            <div className="flex items-center justify-between flex-wrap gap-4">
                                <div className="flex items-center gap-3">
                                    <Film className="h-6 w-6 text-teal-600" />
                                    <div>
                                        <h3 className="font-bold text-lg text-gray-900">
                                            {getCurrentCommunityName()}
                                        </h3>
                                        <p className="text-sm text-gray-600">
                                            {isCurrentCommunityMember() ? 
                                                '✓ You are a member' : 
                                                'Public community'
                                            }
                                        </p>
                                    </div>
                                </div>
                                
                                <div className="flex gap-3">
                                    {isCurrentCommunityMember() && (
                                        <button
                                            className="px-6 py-2.5 bg-teal-600 hover:bg-teal-700 text-white rounded-lg font-semibold transition flex items-center gap-2"
                                            onClick={() => setShowPostCreator(true)}
                                        >
                                            <Plus className="h-4 w-4" />
                                            Create Post
                                        </button>
                                    )}
                                    <button
                                        className="px-4 py-2.5 bg-white border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition"
                                        onClick={() => {
                                            setCurrentCommunityView(null);
                                            loadPosts();
                                        }}
                                    >
                                        View All Communities →
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* INLINE Community Creator */}
                {showCommunityCreator && (
                    <div className="container mx-auto px-8 py-6">
                        <CommunityCreator
                            onSuccess={(newCommunity) => {
                                showToast(`Community "${newCommunity.name}" created! 🎉`, 'success');
                                setCurrentCommunityView(newCommunity.community_id);
                                loadCommunities();
                                if (newCommunity.community_id) {
                                    loadPostsByCommunity(newCommunity.community_id);
                                }
                                setShowCommunityCreator(false);
                                window.scrollTo({ top: 0, behavior: 'smooth' });
                            }}
                            onCancel={() => setShowCommunityCreator(false)}
                        />
                    </div>
                )}

                {/* INLINE Post Creator */}
                {showPostCreator && currentCommunityView && (
                    <div className="container mx-auto px-8 py-6">
                        <PostCreator
                            communityId={currentCommunityView}
                            communityName={getCurrentCommunityName()}
                            onSuccess={(newPost) => {
                                showToast('Post created successfully! 🎬', 'success');
                                loadPostsByCommunity(currentCommunityView);
                                setShowPostCreator(false);
                                window.scrollTo({ top: 400, behavior: 'smooth' });
                            }}
                            onCancel={() => setShowPostCreator(false)}
                        />
                    </div>
                )}

                {/* Featured Hero */}
                {!loading && displayedProjects.length > 0 && !showCommunityCreator && !showPostCreator && (
                    <FeaturedHero
                        project={displayedProjects[0]}
                        onViewDetails={setSelectedProject}
                    />
                )}

                {/* ✅ Search, Filter and Tabs - WITH COMMUNITY SWITCHER */}
                <div className="bg-white py-6 border-b sticky top-0 z-40 shadow-sm">
                    <div className="container mx-auto px-8">
                        <div className="flex gap-3 mb-4 items-center flex-wrap">
                            <div className="film-community-search-container flex-1 min-w-[200px]">
                                <Search className="film-community-search-icon" />
                                <input
                                    type="text"
                                    placeholder="Search projects, creators, or categories..."
                                    className="film-community-search-input"
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                />
                            </div>

                            <button className="filter-button" onClick={() => setFilterOpen(true)}>
                                <Filter className="h-5 w-5 mr-2" />
                                Filters
                            </button>

                            {/* ✅ NEW: CommunitySwitcher moved here */}
                            {isAuthenticated() && (
                                <div className="border-l border-gray-300 pl-3">
                                    <CommunitySwitcher
                                        key={communitySwitcherKey}
                                        currentCommunityId={currentCommunityView}
                                        onCommunitySwitch={(community) => {
                                            setCurrentCommunityView(community?.community_id || null);
                                            if (community) {
                                                loadPostsByCommunity(community.community_id);
                                            } else {
                                                loadPosts();
                                            }
                                        }}
                                    />
                                </div>
                            )}

                            <select className="sort-dropdown" value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
                                {sortOptions.map(option => (
                                    <option key={option.value} value={option.value}>{option.label}</option>
                                ))}
                            </select>

                            <div className="view-toggle">
                                <button className={viewMode === 'grid' ? 'active' : ''} onClick={() => setViewMode('grid')}>
                                    <Grid className="h-5 w-5" />
                                </button>
                                <button className={viewMode === 'list' ? 'active' : ''} onClick={() => setViewMode('list')}>
                                    <List className="h-5 w-5" />
                                </button>
                            </div>
                        </div>

                        <div className="flex gap-4 justify-center flex-wrap">
                            {['Trending', 'Latest', 'Most Viewed', 'Featured'].map((tab) => (
                                <button
                                    key={tab}
                                    className={`tab-button ${activeTab === tab.toLowerCase() ? 'active' : ''}`}
                                    onClick={() => setActiveTab(tab.toLowerCase())}
                                >
                                    {tab}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>

                <FilterSidebar
                    isOpen={filterOpen}
                    onClose={() => setFilterOpen(false)}
                    filters={filters}
                    onFilterChange={setFilters}
                />

                {/* Projects Grid */}
                <div className="container mx-auto px-8 py-8">
                    {loading ? (
                        <div className={`projects-${viewMode}`}>
                            {[...Array(6)].map((_, i) => (
                                <SkeletonCard key={i} />
                            ))}
                        </div>
                    ) : displayedProjects.length === 0 ? (
                        <div className="text-center py-12">
                            <AlertCircle className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                            <p className="text-gray-500 text-lg mb-2">No posts found</p>
                            <p className="text-gray-400 text-sm mb-6">
                                {currentCommunityView ? 
                                    'Be the first to create a post in this community!' :
                                    'Create a community and start sharing your work!'
                                }
                            </p>
                            {currentCommunityView && isCurrentCommunityMember() && (
                                <button
                                    className="px-6 py-3 bg-teal-600 hover:bg-teal-700 text-white rounded-lg font-semibold transition inline-flex items-center gap-2"
                                    onClick={() => setShowPostCreator(true)}
                                >
                                    <Plus className="h-5 w-5" />
                                    Create First Post
                                </button>
                            )}
                        </div>
                    ) : (
                        <>
                            <div className={`projects-${viewMode}`}>
                                {displayedProjects.map((project) => (
                                    <Card
                                        key={project.community_post_id}
                                        onMouseEnter={() => setHoveredProject(project.community_post_id)}
                                        onMouseLeave={() => setHoveredProject(null)}
                                        onClick={() => setSelectedProject(project)}
                                    >
                                        <div className="project-thumbnail">
                                            <img
                                                src={project.thumbnail_url || "/api/placeholder/400/300"}
                                                alt={project.title}
                                                className="w-full h-full object-cover"
                                            />
                                            {hoveredProject === project.community_post_id && (
                                                <div className="play-overlay">
                                                    <Play className="h-12 w-12 text-white" />
                                                </div>
                                            )}
                                            <div className="project-overlay">
                                                <div className="project-actions-top">
                                                    <button
                                                        className={`action-button ${likedProjects.has(project.community_post_id) ? 'active' : ''}`}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleLike(project.community_post_id);
                                                        }}
                                                    >
                                                        <Heart className="h-5 w-5" fill={likedProjects.has(project.community_post_id) ? 'currentColor' : 'none'} />
                                                    </button>
                                                    <button
                                                        className={`action-button ${bookmarkedProjects.has(project.community_post_id) ? 'active' : ''}`}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleBookmark(project.community_post_id);
                                                        }}
                                                    >
                                                        <Bookmark className="h-5 w-5" fill={bookmarkedProjects.has(project.community_post_id) ? 'currentColor' : 'none'} />
                                                    </button>
                                                    <QuickActionsMenu
                                                        projectId={project.community_post_id}
                                                        onAction={handleQuickAction}
                                                    />
                                                </div>

                                                <div className="project-stats">
                                                    <div className="stat-item">
                                                        <Heart className="h-4 w-4" />
                                                        <span>{project.like_count || 0}</span>
                                                    </div>
                                                    <div className="stat-item">
                                                        <MessageCircle className="h-4 w-4" />
                                                        <span>{project.comment_count || 0}</span>
                                                    </div>
                                                    <div className="stat-item">
                                                        <Share2 className="h-4 w-4" />
                                                        <span>{project.share_count || 0}</span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <CardContent>
                                            <div className="flex justify-between items-start mb-2">
                                                <div className="flex-1">
                                                    <h3 className="font-semibold text-lg mb-1">{project.title}</h3>
                                                    <p className="text-sm text-gray-500">{project.community_name || 'Community'}</p>
                                                </div>
                                            </div>

                                            <p className="text-sm text-gray-600 line-clamp-2 mb-2">{project.content}</p>

                                            <div className="flex items-center justify-between text-xs text-gray-500">
                                                <span>{new Date(project.created_at).toLocaleDateString()}</span>
                                            </div>
                                        </CardContent>
                                    </Card>
                                ))}
                            </div>

                            {visibleProjects < allProjects.length && (
                                <div className="text-center mt-8">
                                    <button className="load-more-button" onClick={loadMoreProjects}>
                                        Load More Projects
                                        <ChevronDown className="h-5 w-5 ml-2" />
                                    </button>
                                </div>
                            )}
                        </>
                    )}
                </div>

                {/* Back to Top */}
                {showBackToTop && (
                    <button className="back-to-top" onClick={scrollToTop}>
                        <ArrowUp className="h-5 w-5" />
                    </button>
                )}

                {/* Floating Create Button */}
                {isAuthenticated() && currentCommunityView && isCurrentCommunityMember() && (
                    <button
                        className="floating-create-button"
                        onClick={() => setShowPostCreator(true)}
                        title="Create Post"
                    >
                        <Plus className="h-6 w-6" />
                    </button>
                )}
            </div>
            
            {/* ✅ ADD CSS FOR SCROLL EFFECT */}
            <style jsx>{`
                .hero-shrink {
                    padding-top: 2rem !important;
                    padding-bottom: 2rem !important;
                }
                
                .sticky-search {
                    position: sticky;
                    top: 0;
                    backdrop-filter: blur(10px);
                    background-color: rgba(255, 255, 255, 0.95);
                }
            `}</style>
        </>
    );
};

export default FilmCommunityPage;
